import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { Link } from "react-router-dom";
import { RefreshCw, Loader2, Star, Lock, Crown, ArrowRight } from "lucide-react";
import RevisionCycles from "../components/RevisionCycles";

const PRETTY = {
  clarity: "Clarity",
  evidence: "Evidence",
  sentence_combining: "Sentence Combining",
  elaboration: "Elaboration",
  transitions: "Transitions",
  organization: "Organization",
  voice: "Voice",
  audience_awareness: "Audience Awareness",
  sentence_fluency: "Sentence Fluency",
  rhetorical_impact: "Rhetorical Impact",
  analytical_depth: "Analytical Depth",
  mla_formatting: "MLA Formatting",
};

export default function RevisionIntelligence() {
  const { user } = useAuth();
  const [draft, setDraft] = useState("");
  const [purpose, setPurpose] = useState("argumentative essay");
  const [essayTitle, setEssayTitle] = useState("");
  const [selected, setSelected] = useState(["clarity", "evidence", "elaboration"]);
  const [dims, setDims] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cycles, setCycles] = useState(null);

  const loadCycles = useCallback(() => {
    api.get("/revision/cycles").then(r => setCycles(r.data)).catch(() => {});
  }, []);

  useEffect(() => { api.get("/revision/dimensions").then(r => setDims(r.data)); }, []);
  useEffect(() => { loadCycles(); }, [loadCycles]);

  const maxAllowed = dims?.is_premium ? dims?.premium_max_dimensions : dims?.free_max_dimensions;
  const toggle = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(s => s !== id));
    } else {
      if (maxAllowed && selected.length >= maxAllowed) {
        toast.error(dims.is_premium ? `Max ${maxAllowed} dimensions per analysis.` : `Free plan: max ${maxAllowed} dimensions. Upgrade for ${dims.premium_max_dimensions}.`);
        return;
      }
      setSelected([...selected, id]);
    }
  };

  const analyze = async () => {
    if (!draft.trim()) { toast.error("Paste a draft first."); return; }
    if (selected.length === 0) { toast.error("Pick at least one revision focus."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/revision/analyze", { draft, purpose, focus: selected, essay_title: essayTitle });
      setResult(data);
      celebrateBadges(data._meta);
      loadCycles();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(e.response.data.detail.message);
      } else {
        toast.error("Could not analyze.");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-rose-100 text-rose-700 grid place-items-center"><RefreshCw size={20}/></div>
            <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Revision Intelligence</h1>
            {user?.is_premium && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <Crown size={12}/> Premium
              </span>
            )}
          </div>
          <p className="text-slate-600 max-w-2xl">A writing-workshop coach: scores your draft across multiple dimensions, shows weak vs. improved excerpts, and tells you exactly what to attack in the next draft.</p>
        </div>
        {!user?.is_premium && (
          <Link to="/app/upgrade" data-testid="revision-upgrade-link" className="rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold px-4 py-2 text-sm inline-flex items-center gap-2 tactile-shadow-amber">
            <Crown size={14}/> Upgrade
          </Link>
        )}
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Essay title <span className="text-slate-400">(optional · groups your drafts)</span></label>
            <input data-testid="revision-essay-title" value={essayTitle} onChange={(e)=>setEssayTitle(e.target.value)}
              placeholder='e.g. "Should AI grade student essays?"'
              className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-rose-400 px-4 py-3 outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Purpose / genre</label>
            <input data-testid="revision-purpose" value={purpose} onChange={(e)=>setPurpose(e.target.value)}
              placeholder="e.g. argumentative essay, narrative, DBQ response…"
              className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-rose-400 px-4 py-3 outline-none" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-xs uppercase font-bold tracking-widest text-slate-500">
              Revision focus — pick up to {maxAllowed || 3}
            </label>
            <span className="text-xs text-slate-500">
              {selected.length}/{maxAllowed || 3} selected
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {(dims?.dimensions || Object.keys(PRETTY)).map((d, idx) => {
              const active = selected.includes(d);
              const lockedForFree = !dims?.is_premium && idx >= 3 && !active;
              return (
                <button key={d} data-testid={`revision-dim-${d}`} type="button"
                  disabled={lockedForFree && false /* allow toggle; we cap on action */}
                  onClick={()=>toggle(d)}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold border-2 transition ${active ? 'bg-rose-500 border-rose-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-rose-300'}`}>
                  {PRETTY[d] || d}
                  {lockedForFree && <Lock size={10} className="inline ml-1"/>}
                </button>
              );
            })}
          </div>
          {!dims?.is_premium && (
            <p className="text-xs text-slate-500 mt-2">Free plan analyzes up to 3 dimensions. Premium unlocks 6 per analysis + advanced dimensions like rhetorical impact and MLA formatting.</p>
          )}
        </div>

        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Your draft</label>
          <textarea data-testid="revision-draft" value={draft} onChange={(e)=>setDraft(e.target.value)} rows={10}
            placeholder="Paste your draft here. We'll never rewrite it — we'll show you HOW to revise it."
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-rose-400 px-4 py-3 outline-none resize-none" />
        </div>

        <button data-testid="revision-analyze" onClick={analyze} disabled={loading}
          className="bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-full px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
          style={{boxShadow:'0 4px 0 0 rgb(190 18 60)'}}>
          {loading ? <><Loader2 className="animate-spin" size={16}/> Analyzing…</> : "Run revision analysis"}
        </button>
      </div>

      {result && (
        <div data-testid="revision-result" className="space-y-4 fade-up">
          <div className="tactile-card p-6 bg-indigo-50 border-indigo-100">
            <div className="text-xs uppercase font-bold tracking-widest text-indigo-800 mb-2">Coach's take</div>
            <p className="text-slate-900">{result.overall}</p>
          </div>

          {(result.dimensions || []).map((d, i) => (
            <div key={i} data-testid={`revision-dim-result-${d.name}`} className="tactile-card p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-bold" style={{fontFamily:'Outfit'}}>{PRETTY[d.name] || d.name}</h3>
                <div className="flex items-center gap-1">
                  {[1,2,3,4].map(n => (
                    <Star key={n} size={16} className={n <= d.score ? 'fill-amber-400 text-amber-400' : 'text-slate-300'} />
                  ))}
                  <span className="text-sm font-bold ml-2 text-slate-600">{d.score}/4</span>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="rounded-2xl bg-rose-50 border border-rose-100 p-4">
                  <div className="text-xs uppercase font-bold tracking-widest text-rose-800 mb-1">From your draft</div>
                  <p className="italic text-slate-800">"{d.weak_excerpt}"</p>
                </div>
                <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                  <div className="text-xs uppercase font-bold tracking-widest text-emerald-800 mb-1">Improved example</div>
                  <p className="italic text-slate-800">"{d.improved_example}"</p>
                </div>
              </div>
              <div className="mt-3 grid md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-1">Why it matters</div>
                  <p className="text-slate-700">{d.why_it_matters}</p>
                </div>
                <div>
                  <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-1">How to revise</div>
                  <p className="text-slate-700">{d.how_to_revise}</p>
                </div>
              </div>
            </div>
          ))}

          {result.next_draft_focus && (
            <div className="tactile-card p-6 bg-amber-50 border-amber-100">
              <div className="text-xs uppercase font-bold tracking-widest text-amber-800 mb-2">Next draft — focus here first</div>
              <p className="text-slate-900 font-semibold">{result.next_draft_focus}</p>
            </div>
          )}

          {result.revision_questions?.length > 0 && (
            <div className="tactile-card p-6">
              <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Ask yourself before revising</div>
              <ul className="list-decimal pl-5 space-y-1 text-slate-800">
                {result.revision_questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          )}

          <div className="text-center pt-2">
            <button onClick={() => { setResult(null); }} data-testid="revision-new-draft"
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-6 py-3 inline-flex items-center gap-2 tactile-shadow">
              Revise and re-analyze <ArrowRight size={14}/>
            </button>
          </div>
        </div>
      )}

      {cycles && <RevisionCycles essays={cycles.essays} totalRevisions={cycles.total_revisions} />}
    </div>
  );
}
